import axios from 'axios';
import _ from 'lodash';
import {
  checkAuthentication,
  getAccessToken,
  getTokenExpiration,
  isTimestampExpired,
  isTokenExpired,
} from 'helpers/auth.clientless';
import {
  getAccountCookie,
  getAnonymousAccountCookie,
  removeAccountCookie,
  setAnonymousAccountCookie,
} from 'helpers/Account';
import {
  getCookie,
  getLocalStorageItem,
  removeLocalStorageItem,
  removeCookie,
} from 'helpers/Storage';
import { upgradeJWT } from 'helpers/AccountHelper';
import {
  ANONYMOUS_ACCOUNT_TYPE,
  getCurrentAccount,
  handleAnonymousLogin,
  initAnonymousId,
} from 'helpers/AnonymousHelper';
import { helperGetApiKey, helperGetApiHeaders, helperGetApiAccountRoute } from 'helpers/ApiHelper';
import {
  BUYTHROUGH_DATA_COOKIE_NAME as buythroughDataCookieName,
  MVPD_COOKIE_NAME as mvpdCookieName,
  MVPD_LOGOUT_COOKIE_NAME as mvpdLogoutCookieName,
  SUCCESS_COOKIE_NAME as mvpdSuccessCookieName,
} from 'helpers/AuthHelper';
import {
  getDeviceIdOverride,
  getRequestorId,
  getAuthEndpointConfig,
  getDeviceType,
  clientlessTokenExpireRefresh,
  tveEnabled,
} from 'helpers/ConfigHelper';
import { helperGoToRelativeUrl, redirectToExternalUrl } from 'helpers/RouteHelper';
import {
  clearLongTimeout,
  getMVPDCookie,
  getMVPDFromCookie,
  getMVPDLogoutCookie,
  setLongTimeout,
  isMvpdSubscription,
  urlIsExternal,
} from 'helpers/ContentHelper';
import {
  AUTH_EXPIRED,
  userAttributeEvents as optimizelyUserAttributeEvents,
} from 'helpers/OptimizelyHelper';
import { isPreviewPass } from 'helpers/PlayerHelper';
import { getSegmentDeviceIdCookie } from '@foxcorp/delta-web-analytics';
import { setUserAuthData } from 'index/AppActions';

const MILLISECONDS = 1000;
const TIME_BEFORE_TOKEN_EXPIRE_MS = 10 * 60 * MILLISECONDS;
const getDeviceIdFromCookies = (cookies = []) => {
  const deviceIdCookie = cookies.find(cookie => _.get(cookie, 'deviceId'));
  return _.get(deviceIdCookie, 'deviceId', initAnonymousId());
};

const login = function login(deviceId, accessToken = null) {
  const headers = helperGetApiHeaders({ accessToken, deviceId });
  const route = helperGetApiAccountRoute('login');
  const body = { deviceId };
  if (!route || !headers || !body || !deviceId) {
    return Promise.reject(new Error('Login - requires route, headers, body, or device id'));
  }
  return new Promise(async (resolve, reject) => {
    try {
      const response = await axios.post(route, body, headers);
      const data = _.get(response, 'data');
      const status = _.get(response, 'status');
      const dataStatus = _.get(data, 'status', status);
      if (!data || dataStatus !== 200) {
        return reject(new Error('Login - No Data or response status is not 200'));
      }
      return resolve(data);
    } catch (e) {
      return reject(e);
    }
  });
};

export const getDefaultClientlessOptions = () => {
  const authEndpointConfig = getAuthEndpointConfig();
  const apiKey = helperGetApiKey();
  const requestor = getRequestorId();
  const deviceType = getDeviceType();

  const options = {
    authEndpointConfig: { ...authEndpointConfig, apiKey },
    deviceType,
    requestor,
    mvpdCookieName,
  };

  return options;
};

const checkAuth = async ({ accessToken, deviceId, setMVPDToAppState }) => {
  try {
    const authEndpointConfig = _.get(window.ExternalConfig, 'api.auth');
    const apiKey = helperGetApiKey();
    const checkAuthOptions = {
      authEndpointConfig: { ...authEndpointConfig, apiKey },
      accessToken,
      deviceId,
      mvpdCookieName,
      setMVPDToAppState,
    };
    return await checkAuthentication(checkAuthOptions);
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const fetchAnonymousDataAfterLogout = async ({
  anonymousLogin,
  deviceId,
  setMVPDToAppState,
}) => {
  const anonymousLoginResult = await anonymousLogin({ deviceId });
  const newAnonymousAccount = _.get(anonymousLoginResult, 'data');
  const anonymousAccountAccessToken = getAccessToken(newAnonymousAccount);
  if (!tveEnabled()) return anonymousAccountAccessToken;

  const newMvpdData = await checkAuth({
    accessToken: anonymousAccountAccessToken,
    deviceId,
    setMVPDToAppState,
  });
  const newAuthData = newMvpdData || newAnonymousAccount;
  const newAccessToken = getAccessToken(newAuthData);
  return newAccessToken;
};

export async function handleRefreshToken(currentAuthData) {
  const { refreshTokenTimeout } = this.state || {};
  const { deviceId, setUserAuthData } = this.props || {};
  const tokenExpiration = getTokenExpiration(currentAuthData);
  if (!tokenExpiration) return;

  if (refreshTokenTimeout) clearLongTimeout(refreshTokenTimeout);
  const currentTime = new Date().getTime();
  const refreshOverride = clientlessTokenExpireRefresh();
  const defaultRefresh =
    (tokenExpiration - currentTime - TIME_BEFORE_TOKEN_EXPIRE_MS) * MILLISECONDS;
  const refreshJWTDelay = refreshOverride > MILLISECONDS ? refreshOverride : defaultRefresh;

  const timeoutCallback = async () => {
    let accountResponse;
    try {
      accountResponse = await login(deviceId);
      setAnonymousAccountCookie(accountResponse);
      removeAccountCookie();
      setUserAuthData({
        account: null,
        anonymousAccount: accountResponse,
      });
    } catch (e) {
      console.error('refresh jwt anon login failed');
    }

    const shouldCheckAuth = accountResponse && tveEnabled();
    if (!shouldCheckAuth) return;
    const accountAccessToken = getAccessToken(accountResponse);

    try {
      const options = {
        ...getDefaultClientlessOptions(),
        accessToken: accountAccessToken,
        deviceId,
        setMVPDToAppState: this.setMVPDToAppState,
      };
      await checkAuthentication(options);
      // check auth sets the mvpd cookie and fires setMVPDToAppState for us
    } catch (e) {
      console.error('refresh jwt mvpd authentication was unsuccessful');
    }
  };

  const newRefreshTokenTimeout = setLongTimeout(timeoutCallback, refreshJWTDelay);
  this.setState({ refreshTokenTimeout: newRefreshTokenTimeout });
}

export const getStoredAuthenticatedMVPD = () => {
  const mvpd = getMVPDFromCookie();
  const isAuthenticated = mvpd && !isPreviewPass(mvpd);
  return isAuthenticated;
};

const clearExpiredTokens = async ({
  anonymousLogin,
  deviceId,
  initialAccount,
  initialAnonymousAccount,
  initialMvpd,
  initialMvpdLogoutData,
}) => {
  let account = initialAccount;
  let anonymousAccount = initialAnonymousAccount;
  let mvpd = initialMvpd;
  let mvpdLogoutData = initialMvpdLogoutData;

  if (initialAccount && isTokenExpired(initialAccount)) {
    removeAccountCookie();
    account = null;
    anonymousAccount = await anonymousLogin({ deviceId });
  }

  if (!initialAccount && initialAnonymousAccount && isTokenExpired(initialAnonymousAccount)) {
    anonymousAccount = await anonymousLogin({ deviceId });
  }

  if (initialMvpd && isTokenExpired(initialMvpd)) {
    removeCookie({ key: mvpdCookieName, options: { path: '/' } });
    removeCookie({ key: mvpdSuccessCookieName, options: { path: '/' } });
    optimizelyUserAttributeEvents().authState(AUTH_EXPIRED);
    mvpd = null;
  }

  if (initialMvpdLogoutData && isTokenExpired(initialMvpdLogoutData)) {
    removeCookie({ key: mvpdLogoutCookieName, options: { path: '/' } });
    mvpdLogoutData = null;
  }

  return {
    account,
    anonymousAccount,
    mvpd,
    mvpdLogoutData,
  };
};

const getStoredAuthData = (props = {}) => {
  const initialAnonymousAccount = getAnonymousAccountCookie();
  const initialAccount = getAccountCookie();
  const initialMvpd = getMVPDCookie();
  const initialMvpdLogoutData = getMVPDLogoutCookie();
  const { currentDeviceId } = props;
  let deviceId = currentDeviceId;
  let segmentDeviceId;
  if (!currentDeviceId) {
    const deviceIdCookie = getSegmentDeviceIdCookie();
    const cookies = [
      deviceIdCookie,
      initialMvpd,
      initialMvpdLogoutData,
      initialAccount,
      initialAnonymousAccount,
    ];
    segmentDeviceId = getDeviceIdFromCookies(cookies);
    deviceId = getDeviceIdOverride() || segmentDeviceId;
  }

  return {
    deviceId,
    initialAccount,
    initialAnonymousAccount,
    initialMvpd,
    initialMvpdLogoutData,
    segmentDeviceId,
  };
};

export const getLatestAuthData = async ({ anonymousLogin, currentDeviceId }) => {
  const {
    deviceId,
    initialAnonymousAccount,
    initialAccount,
    initialMvpd,
    initialMvpdLogoutData,
    segmentDeviceId,
  } = getStoredAuthData({ currentDeviceId });

  const newAuthData = await clearExpiredTokens({
    anonymousLogin,
    deviceId,
    initialAccount,
    initialAnonymousAccount,
    initialMvpd,
    initialMvpdLogoutData,
  });
  const {
    account: storedAccount,
    anonymousAccount: storedAnonymousAccount,
    mvpd: storedMvpd,
    mvpdLogoutData: storedMvpdLogoutData,
  } = newAuthData;

  const useAnonymousAccount = !storedAccount && storedAnonymousAccount;
  const prioritizedAccountData = useAnonymousAccount ? storedAnonymousAccount : storedAccount;
  const mvpdTokenExpiration = getTokenExpiration(storedMvpd);
  const accountExpiration = getTokenExpiration(prioritizedAccountData);
  const mvpdLogoutExpiration = getTokenExpiration(storedMvpdLogoutData);

  const mvpdExpired = isTimestampExpired(mvpdTokenExpiration);
  const mvpdLogoutExpired = isTimestampExpired(mvpdLogoutExpiration);
  const accountExpired = isTimestampExpired(accountExpiration);

  let latestStoredData = null;

  if (mvpdTokenExpiration || accountExpiration || mvpdLogoutExpiration) {
    const mvpdExpirationLongerThanAccount = mvpdTokenExpiration >= accountExpiration;
    const mvpdExpirationLongerThanLogout = mvpdTokenExpiration > mvpdLogoutExpiration;
    const accountExpirationLongerThanLogout = accountExpiration > mvpdLogoutExpiration;

    if (mvpdExpirationLongerThanAccount && mvpdExpirationLongerThanLogout && !mvpdExpired) {
      latestStoredData = storedMvpd;
    } else if (
      !mvpdExpirationLongerThanAccount &&
      accountExpirationLongerThanLogout &&
      !accountExpired
    ) {
      latestStoredData = prioritizedAccountData;
    } else if (
      !accountExpirationLongerThanLogout &&
      !mvpdExpirationLongerThanLogout &&
      !mvpdLogoutExpired
    ) {
      latestStoredData = storedMvpdLogoutData;
    }
  }

  return {
    storedAccount,
    storedAnonymousAccount,
    deviceId,
    latestStoredData,
    segmentDeviceId,
    storedMvpd,
    storedMvpdLogoutData,
  };
};

export const getUserData = async (store, profile) => {
  if (isMvpdSubscription()) {
    const { url: signOutUrl } =
      _.get(window.ExternalConfig, 'profile.dropdownMenu', []).find(
        option => option.id === 'signOut',
      ) || {};
    const currentTime = Date.now();
    const lastClosedTime = getLocalStorageItem({ key: 'app_closed' }) || currentTime;
    const amountOfSecondsSinceClosed = (currentTime - lastClosedTime) / 1000;
    removeLocalStorageItem({ key: 'app_closed' });
    if (amountOfSecondsSinceClosed > 15) {
      if (urlIsExternal(signOutUrl)) {
        redirectToExternalUrl(signOutUrl);
      } else {
        helperGoToRelativeUrl(signOutUrl);
      }
    }
  }

  const saveData = data => store.dispatch(setUserAuthData(data));
  const anonymousLogin = ({ deviceId }) =>
    handleAnonymousLogin({
      authorizationToken: null,
      deviceId,
      setUserAuthData: saveData,
      profile,
    });

  const {
    storedAccount,
    storedAnonymousAccount,
    deviceId,
    latestStoredData,
    segmentDeviceId,
    storedMvpd,
    storedMvpdLogoutData,
  } = await getLatestAuthData({ anonymousLogin });

  const buythroughScenario = _.get(getCookie({ name: buythroughDataCookieName }), 'scenario');
  const anonymousId = deviceId;
  let account = storedAccount;
  let anonymousAccount = storedAnonymousAccount;
  const mvpd = storedMvpd;
  const mvpdLogoutData = storedMvpdLogoutData;
  let currentAccount = await getCurrentAccount({
    account,
    anonymousAccount,
    anonymousLogin,
    deviceId,
  });
  const latestAuthData = latestStoredData || currentAccount || {};
  let authorizationToken = getAccessToken(latestAuthData);
  const isAuthenticated = getStoredAuthenticatedMVPD();

  if (authorizationToken && !_.isEmpty(currentAccount)) {
    try {
      const upgradedAccountAccessToken = await upgradeJWT(authorizationToken, deviceId);
      if (upgradedAccountAccessToken) {
        authorizationToken = upgradedAccountAccessToken;
        currentAccount = {
          ...currentAccount,
          accessToken: upgradedAccountAccessToken,
        };
      }
      const accountType = _.get(currentAccount, 'accountType');
      if (accountType === ANONYMOUS_ACCOUNT_TYPE) {
        anonymousAccount = currentAccount;
        account = null;
      } else {
        anonymousAccount = null;
        account = currentAccount;
      }
    } catch (e) {
      console.log('account token upgrade failed', e);
    }
  }

  saveData({
    account,
    anonymousAccount,
    anonymousId,
    authorizationToken,
    buythroughScenario,
    deviceId,
    isAuthenticated,
    mvpd,
    mvpdLogoutData,
    segmentDeviceId,
  });

  return authorizationToken;
};
